import _, {isUndefined} from 'lodash';
import React, {FC, useEffect} from 'react';
import {connect} from 'react-redux';

import {usePrevious} from '../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {useInitialEffect} from '../../../libs/shared/core/src/helpers/react/hooks/useEffectHelpers';
import {ContactModel} from '../../../libs/shared/core/src/models/api/contactModel';
import {RecipientModel} from '../../../libs/shared/core/src/models/api/recipientModel';
import {globalNamespace} from '../../../libs/shared/core/src/models/helpers/namespaceHelpers';
import {ResolveStateProps} from '../../../libs/shared/core/src/store/helpers/commonReselectHelpers';
import {ResolveDispatchProps} from '../../../libs/shared/core/src/store/helpers/reduxHelpers';
import {ViewerPanelId} from '../../../libs/shared/core/src/store/panels/panelStateBase';
import {
  createSelector,
  makeMapDispatchToProps,
  makeMapStateToProps,
  wrapInShallowSelector,
} from '../../../srcCommon/helpers/reselectHelpers';
import {
  ContactDetailsContainerBaseProps,
  makeGetContactsManagerPanelSelectedCardContact,
  makeGetPopoutPanelSelectedContact,
  makeGetPopupConversation,
  makeGetSelectedCard,
  makeGetSelectedVisualContact,
  makeGetVisualContacts,
} from '../../../srcCommon/inboxes/contactDetails/contactDetailsSelectors';
import {useAppDispatch} from '../../helpers/appReduxHelpers';
import {addAccount} from '../../helpers/models/cardAttributeUpdateHelpers';
import {computeConflictingMergeValues} from '../../helpers/models/cardMergeHelpers';
import {isDomainValidityCheckRequired} from '../../helpers/models/domainHelpers';
import {CardModel, CardTypesEnum} from '../../models/api/cardModel';
import {ConversationModel} from '../../models/api/conversationModel';
import {CustomFieldResourceTypesEnum} from '../../models/api/customFieldModel';
import {ThunkAsyncAction} from '../../store/appThunk';
import {
  showContactDetailsMergeErrorBannerAction,
  updateCardAttributeAsyncAction,
} from '../../store/data/cards/cardsActions';
import {fetchCustomFieldsForResourceTypeAsyncAction} from '../../store/data/customFields/customFieldsActions';
import {getCustomFieldsForAccountsFlat} from '../../store/data/customFields/customFieldsSelectors';
import {getCards} from '../../store/data/dataSelectors';
import {updateContactDetailsSectionExpandedAction} from '../../store/data/localPreferences/localPreferencesActions';
import {getContactDetailsSectionsExpandState} from '../../store/data/localPreferences/localPreferencesSelectors';
import {
  clearMergeAction,
  contactDetailsValidateDomainAsyncAction,
  createCardAndUpdateContactDetailsPanelAction,
  fetchContactDetailsDataAction,
  fetchContactDetailsDataContactsManagerAction,
  mergeAndFetchContactDetailCardsAction,
  navigateContactDetailsAction,
  previewMergeAction,
  updateContactDetailsPanelSelectedContactAction,
  updateContactDetailsSelectedDuplicateCardAction,
} from '../../store/panels/contacts/contactDetailsPanelActions';
import {
  makeGetContactDetailsPanel,
  makeGetDuplicateCard,
} from '../../store/panels/contacts/contactDetailsPanelSelectors';
import {ContactDetailsPanelLocationsEnum} from '../../store/panels/contacts/contactDetailsPanelState';
import {subStateIsMergeSubState} from '../../store/panels/contacts/contactDetailsPanelSubState';
import {inLegacyTransientPanel} from '../legacyTransientPanel';
import {ContactDetails} from './contactDetails';
import {ContactDetailsTrackEventHandler} from './contactDetailsTrackingHelpers';

/*
 * Props.
 */

interface ContactDetailsContainerPublicProps extends ContactDetailsContainerBaseProps {
  viewerPanelId?: ViewerPanelId;
  selectedContactUid?: string;
  selectedCardId?: number;
  conversation?: ConversationModel;
  recipient?: RecipientModel;
  onTrackEvent?: ContactDetailsTrackEventHandler;
  onClose?: () => void;
  onDelete?: (model: CardModel) => void;
}

type ContactDetailsContainerStateProps = ResolveStateProps<typeof mapStateToProps>;
type ContactDetailsContainerDispatchProps = ResolveDispatchProps<typeof mapDispatchToProps>;
type ContactDetailsContainerProps = ContactDetailsContainerPublicProps &
  ContactDetailsContainerStateProps &
  ContactDetailsContainerDispatchProps;

/*
 * Selectors.
 */

function makeGetConflictingMergeValues(
  getContactDetailsPanel: ReturnType<typeof makeGetContactDetailsPanel>,
) {
  return createSelector([getContactDetailsPanel, getCards], (panel, cards) => {
    if (!panel || !panel.subState) {
      return undefined;
    }
    const {subState} = panel;
    if (!subStateIsMergeSubState(subState) || !subState.previewCardMerge) {
      return undefined;
    }

    return computeConflictingMergeValues(
      subState.previewCardMerge,
      _(subState.mergeCardIds)
        .map((cardId) => cards[cardId])
        .compact()
        .value(),
    );
  });
}

function makeGetVisualContactsWithFallback(
  getContactDetailsPanel: ReturnType<typeof makeGetContactDetailsPanel>,
) {
  const getPopupConversation = makeGetPopupConversation();
  const getPopoutSelectedUser = makeGetPopoutPanelSelectedContact(
    getContactDetailsPanel,
    getPopupConversation,
  );
  const getContactsManagerSelectedCardContact =
    makeGetContactsManagerPanelSelectedCardContact(getContactDetailsPanel);

  const getVisualContacts = makeGetVisualContacts(getContactDetailsPanel);

  return createSelector(
    [getPopoutSelectedUser, getContactsManagerSelectedCardContact, getVisualContacts],
    (popoutSelectedUser, contactsManagerCardContact, visualContacts) => {
      // If we have a popout selected user, we should return early
      if (popoutSelectedUser) {
        return [popoutSelectedUser];
      }
      // If we have a contacts manager contact, return this early
      if (contactsManagerCardContact) {
        return [contactsManagerCardContact];
      }
      return visualContacts;
    },
  );
}

function makeGetHasAccountCustomFieldsLoaded() {
  return createSelector([getCustomFieldsForAccountsFlat], (customFields) => customFields.length > 0);
}

/*
 * Component.
 */

const ContactDetailsContainerComponent: FC<ContactDetailsContainerProps> = (props) => {
  const {
    contactDetailsPanel,
    conversation,
    panelId,
    selectedVisualContact,
    selectedCard,
    duplicateCard,
    contacts,
    sectionExpandState,
    conflictingMergeValues,
    selectedCardId,
    hasAccountCustomFieldsLoaded,
    onTrackEvent,
    previewMerge,
    mergeAndFetchContactDetailCards,
    clearMerge,
    onContactDetailsSectionToggled,
    showContactDetailsMergeErrorBanner,
    updateContactDetailsSelectedDuplicateCard,
    onClose,
    fetchCustomFieldsForResourceType,
    addCardToAccount,
    createNewCardAsync,
    validateDomainAsync,
    onDelete,
  } = props;

  const dispatch = useAppDispatch();
  const previousDomainValue = usePrevious(contactDetailsPanel?.selectedDomain);

  // Fetch the account custom fields if they have the FF turned on and we have not loaded them yet.
  useInitialEffect(() => {
    if (!hasAccountCustomFieldsLoaded) {
      fetchCustomFieldsForResourceType(CustomFieldResourceTypesEnum.ACCOUNT);
    }
  });

  // Fetch the details for the selected contact.
  useEffect(() => {
    if (
      selectedVisualContact?.contact.uid &&
      isUndefined(contactDetailsPanel?.status) &&
      contactDetailsPanel?.location !== ContactDetailsPanelLocationsEnum.CONTACTS_MANAGER
    ) {
      dispatch(
        fetchContactDetailsDataAction(
          panelId,
          selectedVisualContact?.contact.uid,
          contactDetailsPanel?.selectedCardId,
        ),
      );
    }
  }, [
    dispatch,
    panelId,
    contactDetailsPanel?.location,
    selectedVisualContact?.contact.uid,
    contactDetailsPanel?.selectedCardId,
    contactDetailsPanel?.status,
  ]);

  // If we are in the contacts manager, we need to listen for changes to the selected card.
  useEffect(() => {
    if (
      selectedCardId &&
      contactDetailsPanel?.location === ContactDetailsPanelLocationsEnum.CONTACTS_MANAGER
    ) {
      dispatch(fetchContactDetailsDataContactsManagerAction(panelId, selectedCardId));
    }
  }, [dispatch, panelId, contactDetailsPanel?.location, selectedCardId]);

  // Check if we have no user selected or an invalid user selected.
  useEffect(() => {
    const hasContactsButNoUserSelected = !selectedVisualContact?.contact.uid && contacts.length > 0;
    const hasContactSelectedButNotInContacts =
      Boolean(selectedVisualContact?.contact.uid) &&
      !contacts.some((contact) => contact.contact.uid === selectedVisualContact?.contact.uid);

    if (hasContactsButNoUserSelected || hasContactSelectedButNotInContacts) {
      dispatch(updateContactDetailsPanelSelectedContactAction(panelId, contacts[0].contact.uid));
    }
  }, [dispatch, panelId, contacts, selectedVisualContact?.contact.uid]);

  // Watch if we need to validate the domain for the card.
  useEffect(() => {
    if (
      isDomainValidityCheckRequired(contactDetailsPanel?.selectedDomain) &&
      contactDetailsPanel?.selectedDomain !== previousDomainValue
    ) {
      validateDomainAsync(panelId);
    }
  }, [panelId, contactDetailsPanel?.selectedDomain, validateDomainAsync, previousDomainValue]);

  if (!contactDetailsPanel) {
    return null;
  }

  return (
    <ContactDetails
      panelId={panelId}
      conversation={conversation}
      contactDetailsPanel={contactDetailsPanel}
      selectedVisualContact={selectedVisualContact}
      selectedCard={selectedCard}
      duplicateCard={duplicateCard}
      contacts={contacts}
      sectionExpandState={sectionExpandState}
      conflictingMergeValues={conflictingMergeValues}
      onTrackEvent={onTrackEvent}
      previewMerge={previewMerge}
      mergeAndFetchContactDetailCards={mergeAndFetchContactDetailCards}
      clearMerge={clearMerge}
      onContactDetailsSectionToggled={onContactDetailsSectionToggled}
      showContactDetailsMergeErrorBanner={showContactDetailsMergeErrorBanner}
      updateContactDetailsSelectedDuplicateCard={updateContactDetailsSelectedDuplicateCard}
      onClose={onClose}
      addCardToAccount={addCardToAccount}
      createNewCardAsync={createNewCardAsync}
      onDelete={onDelete}
    />
  );
};

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<ContactDetailsContainerPublicProps>()(() => {
  const getContactDetailsPanel = makeGetContactDetailsPanel();
  const getVisualContacts = makeGetVisualContactsWithFallback(getContactDetailsPanel);
  const getSelectedCard = makeGetSelectedCard(getContactDetailsPanel);
  const getDuplicateCard = makeGetDuplicateCard(getContactDetailsPanel, getSelectedCard);

  return {
    contacts: getVisualContacts,
    contactDetailsPanel: getContactDetailsPanel,
    selectedVisualContact: makeGetSelectedVisualContact(getContactDetailsPanel, getVisualContacts),
    conflictingMergeValues: makeGetConflictingMergeValues(getContactDetailsPanel),
    selectedCard: getSelectedCard,
    duplicateCard: getDuplicateCard,
    hasAccountCustomFieldsLoaded: makeGetHasAccountCustomFieldsLoaded(),
    sectionExpandState: wrapInShallowSelector(getContactDetailsSectionsExpandState),
  };
});

const mapDispatchToProps = makeMapDispatchToProps<ContactDetailsContainerPublicProps>()(({panelId}) => ({
  previewMerge: previewMergeAction,
  mergeAndFetchContactDetailCards: mergeAndFetchContactDetailCardsAction,
  clearMerge: clearMergeAction,
  onContactDetailsSectionToggled: updateContactDetailsSectionExpandedAction,
  showContactDetailsMergeErrorBanner: showContactDetailsMergeErrorBannerAction,
  updateContactDetailsSelectedDuplicateCard: updateContactDetailsSelectedDuplicateCardAction,
  fetchCustomFieldsForResourceType: fetchCustomFieldsForResourceTypeAsyncAction,
  validateDomainAsync: contactDetailsValidateDomainAsyncAction,
  addCardToAccount: (accountId: number, card: CardModel) =>
    updateCardAttributeAsyncAction({
      card,
      action: addAccount(accountId),
      shouldShowUndoBanner: true,
      panelId,
    }),
  createNewCardAsync:
    (name: string, contact: ContactModel, namespace?: string): ThunkAsyncAction<CardModel | undefined> =>
    async (dispatch) => {
      const createCard = await dispatch(
        createCardAndUpdateContactDetailsPanelAction(
          panelId,
          {
            // If we do nto have a namespace passed in, we can assume this should be created globally
            namespace: namespace ?? globalNamespace,
            name,
            contacts: [contact],
            type: CardTypesEnum.AUTO,
          },
          {shouldHideLoadingState: true},
        ),
      );
      return createCard;
    },
}));

export const ContactDetailsContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
)(ContactDetailsContainerComponent);

export const TransientContactDetailsContainer = inLegacyTransientPanel(
  ContactDetailsContainer,
  ({panelId, viewerPanelId, recipient, selectedCardId, location}) =>
    navigateContactDetailsAction(panelId, recipient?.contactUid, selectedCardId, location, viewerPanelId),
);
